import { INSTITUTIONS_ENABLED } from './featureFlags';

export const INSTITUTION_STUDENT_ONBOARDING_PATH = '/institutions/student-onboarding';
export const STUDENT_HOME_PATH = '/home';
export const STUDENT_WORKSPACE_SESSION_KEY = 'student_workspace';
export const STUDENT_WORKSPACE_REMEMBER_KEY = 'student_workspace_remember';

/** @deprecated use STUDENT_WORKSPACE_REMEMBER_KEY */
export const STUDENT_HOME_PREFERENCE_KEY = STUDENT_WORKSPACE_REMEMBER_KEY;

export function isActiveInstitutionStudentMembership(membership) {
  if (!membership || membership.member_role !== 'student') return false;
  if (membership.member_status && membership.member_status !== 'active') return false;
  const status = membership.institution?.status;
  return status === 'trial' || status === 'active';
}

export function getActiveStudentMemberships(studentMemberships = []) {
  return studentMemberships.filter(isActiveInstitutionStudentMembership);
}

export function getInstitutionStudentMembership(studentMemberships = []) {
  return getActiveStudentMemberships(studentMemberships)[0] || null;
}

export function hasAnyInstitutionStudentAccess(memberships = []) {
  if (!INSTITUTIONS_ENABLED) return false;
  return getActiveStudentMemberships(
    memberships.filter((m) => m.member_role === 'student'),
  ).length > 0;
}

export function getStudentMembershipForSlug(studentMemberships = [], slug) {
  if (!slug) return null;
  return getActiveStudentMemberships(studentMemberships).find(
    (m) => m.institution?.slug === slug,
  ) || null;
}

export function needsInstitutionStudentOnboarding(user, studentMemberships = []) {
  if (!INSTITUTIONS_ENABLED || !user || user.onboarding_completed !== false) return false;
  return Boolean(getInstitutionStudentMembership(studentMemberships));
}

export function institutionStudentPortalPath(membership) {
  const slug = membership?.institution?.slug;
  return slug ? `/i/${slug}` : null;
}

export function classroomWorkspaceValue(slug) {
  return slug ? `classroom:${slug}` : null;
}

export function parseClassroomSlug(workspace) {
  if (!workspace || !workspace.startsWith('classroom:')) return null;
  return workspace.slice('classroom:'.length) || null;
}

export function userHasActiveSubscription(user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const tier = (user.subscription_tier || '').toLowerCase();
  if (!tier || tier === 'free' || tier === 'none') return false;
  return true;
}

export function isClassroomOnlyInstitutionStudent(memberships = [], hasActiveSubscription = false) {
  if (!INSTITUTIONS_ENABLED || hasActiveSubscription) return false;
  const active = getActiveStudentMemberships(
    memberships.filter((m) => m.member_role === 'student'),
  );
  if (active.length === 0) return false;
  return active.every((m) => !m.platform_access);
}

export function institutionClassroomPath(memberships = [], hasActiveSubscription = false) {
  const membership = getInstitutionStudentMembership(
    memberships.filter((m) => m.member_role === 'student'),
  );
  if (!membership) return null;
  return institutionStudentPortalPath(membership);
}

export function getSessionWorkspace() {
  try {
    const session = sessionStorage.getItem(STUDENT_WORKSPACE_SESSION_KEY);
    if (session) return session;
    const remembered = localStorage.getItem(STUDENT_WORKSPACE_REMEMBER_KEY);
    if (remembered) return remembered;
  } catch {
    /* ignore */
  }
  return null;
}

export function setSessionWorkspace(workspace, remember = false) {
  try {
    if (workspace) {
      sessionStorage.setItem(STUDENT_WORKSPACE_SESSION_KEY, workspace);
      if (remember) {
        localStorage.setItem(STUDENT_WORKSPACE_REMEMBER_KEY, workspace);
      }
    } else {
      sessionStorage.removeItem(STUDENT_WORKSPACE_SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearSessionWorkspace() {
  try {
    sessionStorage.removeItem(STUDENT_WORKSPACE_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function resolveWorkspacePath(workspace, memberships = []) {
  if (!workspace) return null;
  if (workspace === 'platform') return '/dashboard';
  const slug = parseClassroomSlug(workspace);
  if (slug) {
    const membership = getStudentMembershipForSlug(
      memberships.filter((m) => m.member_role === 'student'),
      slug,
    );
    if (membership) return institutionStudentPortalPath(membership);
    return `/i/${slug}`;
  }
  return null;
}

export function resolveStudentHomeDestination(memberships = [], hasActiveSubscription = false) {
  if (!hasAnyInstitutionStudentAccess(memberships)) {
    return '/dashboard';
  }
  const workspace = getSessionWorkspace();
  const resolved = resolveWorkspacePath(workspace, memberships);
  if (resolved) return resolved;
  return STUDENT_HOME_PATH;
}

export function needsStudentDestinationHub(memberships = []) {
  return hasAnyInstitutionStudentAccess(memberships);
}

export function studentNeedsWorkspaceChoice(memberships = []) {
  if (!hasAnyInstitutionStudentAccess(memberships)) return false;
  return !getSessionWorkspace();
}

export function safeAppPath(value) {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  return path;
}

export function resolvePostLoginPath({
  redirectTo,
  memberships = [],
  hasActiveSubscription = false,
} = {}) {
  const redirect = safeAppPath(redirectTo);
  if (redirect) return redirect;
  return resolveStudentHomeDestination(memberships, hasActiveSubscription);
}

export function resolvePostOnboardingPath(memberships = [], hasActiveSubscription = false) {
  return resolveStudentHomeDestination(memberships, hasActiveSubscription);
}

/** @deprecated */
export function getSavedStudentHomePreference() {
  const w = getSessionWorkspace();
  if (w === 'platform') return 'platform';
  if (w?.startsWith('classroom:')) return 'classroom';
  return null;
}

/** @deprecated */
export function saveStudentHomePreference(preference, slug) {
  if (preference === 'platform') setSessionWorkspace('platform', true);
  else if (preference === 'classroom' && slug) setSessionWorkspace(classroomWorkspaceValue(slug), true);
}
